import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { toast } from "sonner";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BoostButton } from "@/components/promo/FlashCreditsWidgets";
import {
    Eye,
    MousePointerClick,
    TicketPercent,
    TicketCheck,
    ChevronLeft,
    Archive,
    Send,
    XCircle,
    CheckCircle2,
    Clock,
    Link2,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TravelPosterCTAs, InclusionsList } from "@/components/posts/TravelPosterCTAs";

const CHART_COLOR = "#FF7A1A";

const STATUS_TONE = {
    PUBLISHED: "bg-emerald-100 text-emerald-700 border-emerald-200",
    DRAFT: "bg-neutral-100 text-neutral-700 border-neutral-200",
    EXPIRED: "bg-rose-100 text-rose-700 border-rose-200",
    ARCHIVED: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function MerchantOfferDetail() {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [coupons, setCoupons] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState("");
    const [redeemResult, setRedeemResult] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const [p, a] = await Promise.all([
                api.get(`/posts/${id}`),
                api.get(`/me/posts/${id}/analytics`),
            ]);
            setPost(p.data);
            setAnalytics(a.data);
            if (p.data?.details?.coupon_enabled) {
                const c = await api.get(`/me/posts/${id}/coupons?limit=100`);
                setCoupons(c.data);
            } else {
                setCoupons(null);
            }
        } catch (e) {
            toast.error("Could not load offer");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const chartData = useMemo(() => {
        return (analytics?.daily_series || []).map((p) => ({
            date: p.date?.slice(5),
            views: p.views,
            engagements: p.engagements,
        }));
    }, [analytics]);

    const doPublish = async () => {
        try {
            setBusy(true);
            await api.post(`/posts/${id}/publish`);
            await load();
            toast.success("Published");
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const doArchive = async () => {
        try {
            setBusy(true);
            await api.post(`/posts/${id}/archive`);
            await load();
            toast.success("Archived");
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const redeem = async () => {
        const c = (code || "").trim().toUpperCase();
        if (!c) return;
        try {
            setBusy(true);
            const r = await api.post(`/merchant/coupons/redeem`, { code: c });
            setRedeemResult({ ok: true, ...r.data });
            toast.success(r.data.status === "REDEEMED" ? "Redeemed ✔" : r.data.status);
            setCode("");
            await load();
        } catch (e) {
            const msg = errorMessage(e);
            setRedeemResult({ ok: false, message: msg });
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <MerchantLayout>
            <div className="px-4 pb-6 pt-3" data-testid="merchant-offer-detail-page">
                <div className="flex items-center justify-between">
                    <Link
                        to="/merchant/offers"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600"
                        data-testid="mod-back-link"
                    >
                        <ChevronLeft size={14} /> All offers
                    </Link>
                    {post?.status && (
                        <Badge
                            variant="outline"
                            className={`uppercase text-[10px] ${STATUS_TONE[post.status] || ""}`}
                        >
                            {post.status}
                        </Badge>
                    )}
                </div>

                {loading || !post || !analytics ? (
                    <div className="mt-4 space-y-3">
                        <Skeleton className="h-40 rounded-2xl" />
                        <Skeleton className="h-24 rounded-2xl" />
                        <Skeleton className="h-40 rounded-2xl" />
                    </div>
                ) : (
                    <>
                        <Card className="mt-3 overflow-hidden rounded-2xl border border-neutral-200">
                            {post.poster_image_url && (
                                <div className="aspect-[4/3] bg-neutral-100">
                                    <img
                                        src={publicAssetUrl(post.poster_image_url)}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                            )}
                            <div className="p-4">
                                <h1 className="text-lg font-extrabold text-neutral-900" data-testid="mod-title">
                                    {post.title}
                                </h1>
                                {post.description && (
                                    <p className="mt-1 text-sm text-neutral-600">{post.description}</p>
                                )}
                                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                    {post.details?.discount_percent != null && (
                                        <Chip>{post.details.discount_percent}% off</Chip>
                                    )}
                                    <Chip>{post.district_slug}</Chip>
                                    {post.valid_to && (
                                        <Chip tone="rose">
                                            Until {new Date(post.valid_to).toLocaleDateString()}
                                        </Chip>
                                    )}
                                </div>
                                {post.details?.booking_cta_type && (
                                    <TravelPosterCTAs post={post} className="mt-4" />
                                )}
                                <InclusionsList
                                    inclusions={post.details?.inclusions}
                                    className="mt-4"
                                />
                                {post.status === "PUBLISHED" && post.type === "OFFER" && (
                                    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="mod-boost-row">
                                        <BoostButton post={post} />
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* KPIs */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <MiniKpi
                                icon={<Eye size={14} />}
                                label="Views"
                                value={analytics.total_views}
                                testid="mod-kpi-views"
                            />
                            <MiniKpi
                                icon={<MousePointerClick size={14} />}
                                label="Engagements"
                                value={analytics.total_engagements}
                                testid="mod-kpi-engagements"
                            />
                            <MiniKpi
                                icon={<Link2 size={14} />}
                                label="Partner Clicks"
                                value={analytics.engagements_by_type?.PARTNER_LINK ?? 0}
                                testid="mod-kpi-partner-clicks"
                            />
                            {analytics.coupons?.enabled && (
                                <>
                                    <MiniKpi
                                        icon={<TicketPercent size={14} />}
                                        label="Claims"
                                        value={analytics.coupons.claims_count}
                                        testid="mod-kpi-claims"
                                    />
                                    <MiniKpi
                                        icon={<TicketCheck size={14} />}
                                        label="Redeemed"
                                        value={analytics.coupons.redeemed_count}
                                        testid="mod-kpi-redeemed"
                                    />
                                </>
                            )}
                        </div>

                        {/* Daily chart */}
                        <Card className="mt-4 rounded-2xl border border-neutral-200 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-bold text-neutral-800">14-day activity</div>
                                    <div className="text-[11px] text-neutral-500">Daily views for this offer</div>
                                </div>
                            </div>
                            <div className="mt-3 h-40 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                                        <CartesianGrid stroke="#f3f4f6" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                                        <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" allowDecimals={false} />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: "1px solid #e5e7eb",
                                                fontSize: 12,
                                            }}
                                        />
                                        <Bar dataKey="views" fill={CHART_COLOR} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>

                        {/* Redeem widget */}
                        {post.details?.coupon_enabled && (
                            <Card className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 p-4" data-testid="mod-redeem-card">
                                <div className="mb-2 text-sm font-bold text-neutral-800">
                                    Redeem a coupon
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input
                                        className="h-12 bg-white text-base uppercase"
                                        placeholder="ABCD-EFGH-JKMN"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        data-testid="mod-redeem-input"
                                    />
                                    <Button
                                        disabled={!code || busy}
                                        onClick={redeem}
                                        className="h-12 nest-gradient font-semibold text-white"
                                        data-testid="mod-redeem-btn"
                                    >
                                        Redeem
                                    </Button>
                                </div>
                                {redeemResult && (
                                    <div
                                        className={`mt-3 flex items-start gap-2 rounded-xl p-3 text-xs ${redeemResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}
                                        data-testid="mod-redeem-result"
                                    >
                                        {redeemResult.ok ? (
                                            <CheckCircle2 className="mt-0.5" size={14} />
                                        ) : (
                                            <XCircle className="mt-0.5" size={14} />
                                        )}
                                        <div>
                                            {redeemResult.ok
                                                ? redeemResult.status === "REDEEMED"
                                                    ? `Code ${redeemResult.code} redeemed successfully.`
                                                    : `This code was already redeemed.`
                                                : redeemResult.message}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )}

                        {/* Claims list */}
                        {coupons && (
                            <div className="mt-6">
                                <div className="mb-2 flex items-center justify-between">
                                    <div className="text-sm font-bold text-neutral-800">
                                        Claims
                                    </div>
                                    <div className="text-[11px] text-neutral-500">
                                        {coupons.counters?.redeemed_count}/{coupons.counters?.claims_count} redeemed
                                    </div>
                                </div>
                                {coupons.claims?.length ? (
                                    <ul className="space-y-2" data-testid="mod-claims-list">
                                        {coupons.claims.map((c) => (
                                            <ClaimRow key={c.code} c={c} />
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center text-xs text-neutral-500">
                                        No claims yet.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-6 flex gap-2">
                            {post.status === "DRAFT" && (
                                <Button
                                    onClick={doPublish}
                                    disabled={busy}
                                    className="h-11 flex-1 nest-gradient text-white"
                                    data-testid="mod-publish-btn"
                                >
                                    <Send size={14} className="mr-2" /> Publish now
                                </Button>
                            )}
                            {post.status !== "ARCHIVED" && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="h-11 flex-1"
                                            data-testid="mod-archive-btn"
                                        >
                                            <Archive size={14} className="mr-2" /> Archive
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Archive this offer?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Customers won't see it in the feed anymore. You
                                                can republish later.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={doArchive}>
                                                Archive
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </div>
                    </>
                )}
            </div>
        </MerchantLayout>
    );
}

function MiniKpi({ icon, label, value, testid }) {
    return (
        <Card className="rounded-2xl border border-neutral-200 p-3" data-testid={testid}>
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase text-neutral-500">
                {icon} {label}
            </div>
            <div className="mt-1 text-xl font-extrabold text-neutral-900">{value ?? 0}</div>
        </Card>
    );
}

function Chip({ children, tone = "neutral" }) {
    const tones = {
        neutral: "bg-neutral-100 text-neutral-700",
        rose: "bg-rose-50 text-rose-700",
        orange: "bg-orange-100 text-orange-700",
    };
    return (
        <span className={`rounded-full px-2 py-1 font-medium ${tones[tone]}`}>{children}</span>
    );
}

function ClaimRow({ c }) {
    const isRedeemed = c.status === "REDEEMED";
    return (
        <li
            className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-3"
            data-testid={`mod-claim-${c.code}`}
        >
            <div className="min-w-0">
                <div className="truncate font-mono text-sm font-bold tracking-wide text-neutral-800">
                    {c.code}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-500">
                    <Clock size={11} />
                    {new Date(c.claimed_at).toLocaleString()}
                    {c.mobile_masked && <span>· {c.mobile_masked}</span>}
                </div>
            </div>
            <Badge
                variant="outline"
                className={`shrink-0 text-[10px] ${isRedeemed ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}
            >
                {c.status}
            </Badge>
        </li>
    );
}
